import React, { useEffect, useState, useMemo } from 'react';
import { 
  Typography, 
  Card, 
  Timeline, 
  Tabs, 
  Spin, 
  Pagination, 
  Empty, 
  Message,
  Space,
  Tag,
  Button
} from '@arco-design/web-react';
import { 
  IconCalendar,
  IconFile,
  IconTag,
  IconDownload
} from '@arco-design/web-react/icon';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchAllRecords } from '../store/slices/updateHistorySlice';
import MarkdownViewer from '../components/common/MarkdownViewer';
import { useLocation, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const TimelineItem = Timeline.Item;
const TabPane = Tabs.TabPane;

const PAGE_SIZE = 20; // 每页显示的记录数量

const HomePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 从Redux获取更新记录数据
  const { records, loading, error } = useAppSelector(state => state.updateHistory);
  
  // 本地状态
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // 获取所有软件名称列表
  const softwareList = useMemo(() => {
    const softwareSet = new Set(records.map(record => record.softwareName));
    return Array.from(softwareSet).reverse(); // 使用reverse()将软件列表逆序排列
  }, [records]);
  
  // 从URL参数中获取选中的软件名称
  const selectedSoftware = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const softName = params.get('softName');
    return softName && softwareList.includes(softName) ? softName : softwareList[0] || '';
  }, [location.search, softwareList]);
  
  // 在组件加载时获取所有记录
  useEffect(() => {
    dispatch(fetchAllRecords());
  }, [dispatch]);
  
  // 错误处理
  useEffect(() => {
    if (error) {
      Message.error(error);
    }
  }, [error]);
  
  // 根据选择的软件过滤记录
  const filteredRecords = useMemo(() => {
    if (!selectedSoftware) {
      return [];
    }
    return records
      .filter(record => record.softwareName === selectedSoftware)
      .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
  }, [records, selectedSoftware]);
  
  // 计算分页数据
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredRecords.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredRecords, currentPage]);
  
  // 处理软件选择变化
  const handleSoftwareChange = (value: string) => {
    // 更新URL参数
    const params = new URLSearchParams(location.search);
    params.set('softName', value);
    navigate({ search: params.toString() });
    setCurrentPage(1); // 切换软件时重置到第一页
  };
  
  // 渲染更新记录时间线
  const renderTimeline = () => {
    if (loading) {
      return <Spin size={40} />;
    }
    
    if (paginatedRecords.length === 0) {
      return <Empty description="暂无更新记录" />;
    }
    
    return (
      <Timeline>
        {paginatedRecords.map((record) => (
          <TimelineItem 
            key={record.id}
            dot={<IconCalendar style={{ fontSize: 16, color: '#165DFF' }} />}
          >
            <Card style={{ marginBottom: 16 }}>
              <Title heading={5} style={{ marginBottom: 8 }}>
                <Space>
                  <span>{record.title || record.softwareName}</span>
                  <Tag color="arcoblue" icon={<IconTag />}>{record.version}</Tag>
                </Space>
              </Title>
              <MarkdownViewer content={record.content} />
              <Space style={{ marginTop: 12, fontSize: 12, color: 'var(--color-text-3)' }}>
                <IconFile /> 更新于 {new Date(record.updateDate).toLocaleString()}
                {record.createdBy && <Text type="secondary">由 {record.createdBy} 创建</Text>}
                {record.downloadLinks && record.downloadLinks.length > 0 && (
                  <Space wrap>
                    {record.downloadLinks.map((link, index) => (
                      <Button
                        key={index}
                        type="text"
                        size="small"
                        icon={<IconDownload />}
                        onClick={() => window.open(link.url, '_blank')}
                      >
                        {link.title}
                      </Button>
                    ))}
                  </Space>
                )}
              </Space>
            </Card>
          </TimelineItem>
        ))}
      </Timeline>
    );
  };
  
  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card style={{ width: '100%' }} title="更新记录">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tabs
                type="card"
                activeTab={selectedSoftware}
                onChange={handleSoftwareChange}
              >
                {softwareList.map(software => (
                  <TabPane key={software} title={software} />
                ))}
              </Tabs>
              
              <Pagination
                total={filteredRecords.length}
                current={currentPage}
                pageSize={PAGE_SIZE}
                onChange={(page: any) => setCurrentPage(Number(page))}
                hideOnSinglePage
                simple
              />
            </div>
            
            <div style={{ minHeight: 300 }}>
              {renderTimeline()}
            </div>
            
            {filteredRecords.length > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Pagination
                  total={filteredRecords.length}
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  onChange={(page: any) => setCurrentPage(Number(page))}
                  showTotal
                />
              </div>
            )}
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default HomePage; 

